import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, catchError, finalize, map, Observable, of, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { PreferenceModel } from '../models/preference/preference-model';
import { ThemeModel } from '../models/preference/theme-model';
import { LanguageService } from './language.service';
import { AiApiService } from './ai-api.service';

export type PreferenceType = PreferenceModel | undefined;

@Injectable({
  providedIn: 'root'
})
export class PreferenceService implements OnDestroy {
  private unsubscribe: Subscription[] = [];
  private prefToken = `${environment.appVersion}-${environment.USERDATA_KEY}-pref`;

  readonly currentPreference: Observable<PreferenceType>;
  private _preferenceSubject: BehaviorSubject<PreferenceType>;

  private _isLanguageUpdate: boolean = false;

  constructor(
    private aiApiService: AiApiService,
    private languageService: LanguageService
  ) {
    this._preferenceSubject = new BehaviorSubject<PreferenceType>(undefined);
    this.currentPreference = this._preferenceSubject.asObservable();
  }

  getPreference() {
    if (!this._preferenceSubject.value) {
      const pref = this.getPreferenceFromLocalStorage();

      let i18nAppLang = localStorage.getItem('locale');

      if (pref) {
        if (i18nAppLang && pref.language != i18nAppLang) {
          pref.language = i18nAppLang;
        }

        if (!pref.theme) {
          pref.theme = new ThemeModel()
        }

        this._preferenceSubject.next(Object.assign({}, pref));
        this.languageService.setLanguage(pref.language);
      }
      else {
        this.aiApiService.getUserPreferences()
          .subscribe((pref: PreferenceModel) => {

            if (i18nAppLang && pref.language != i18nAppLang) {
              pref.language = i18nAppLang;
            }

            if (!pref.theme) {
              pref.theme = new ThemeModel()
            }

            this.setPreferenceToLocalStorage(pref);
            this._preferenceSubject.next(Object.assign({}, pref));

            return pref;
          });
      }
    }
  }

  /**
   * Persists the Preferences without refreshing the current preference observer.
   * The preferences are saved to localStore. 
   * In case were language setting is update a page reload will be triggered and 
   * therefore any subscribers will get a fresh copy of the preferences. 
   * @param pref 
   * @returns true if the rest call was sucessfull.
   */
  updatePreference(pref: PreferenceModel): Observable<boolean> {

    let lang = this.getPreferenceFromLocalStorage()?.language;
    this._isLanguageUpdate = lang != pref.language;

    this.languageService.setLanguage(pref.language);

    return this.aiApiService.updateUserPreference(pref)
      .pipe(
        map((success) => {

          if (success) {

            this.setPreferenceToLocalStorage(pref);

            //if we are updating prefs other than language
            //we dont notify any scubscribers because the page will be reloaded
            // if (!this._isLanguageUpdate) {
            //   this._preferenceSubject.next(Object.assign({}, pref));
            // }
          }

          return success;
        }),
        catchError((err) => {
          return of(err);
        }),
        finalize(() => this._isLanguageUpdate = false)
      );
  }

  /**
  * Persists the Preferences and refreshes the current preference observer with the values.
  * Any observers will be notified with the changes. 
  * The preferences are saved to localStore. 
  * @param pref 
  * @returns true if the rest call was sucessfull.
  */
  updateThemePreference(pref: PreferenceModel): Observable<boolean> {
    return this.aiApiService.updateUserPreference(pref)
      .pipe(
        map((success) => {

          if (success) {
            this.setPreferenceToLocalStorage(pref);
            //this._preferenceSubject.next(Object.assign({}, pref));
          }

          return success;
        }),
        catchError((err) => {
          return of(err);
        })
      );
  }

  private setPreferenceToLocalStorage(pref: PreferenceModel): boolean {

    if (pref) {
      localStorage.setItem(this.prefToken, JSON.stringify(pref));
      return true;
    }
    return false;
  }

  private getPreferenceFromLocalStorage(): PreferenceType {
    try {

      const lsValue = localStorage.getItem(this.prefToken);

      if (!lsValue) {
        return undefined;
      }

      return JSON.parse(lsValue);
    } catch (error) {
      return undefined;
    }
  }

  clear() {
    localStorage.removeItem(this.prefToken);
    this._preferenceSubject.next(undefined);
  }

  needsToViewUpdates(): boolean {
    var pref: PreferenceType = this._preferenceSubject.value;
    if (pref && (pref.webUxVersion != environment.appVersion)) {
      return true;
    } else {
      return false;
    }
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }
}
